import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { PreAuthority } from '../../../model/preAuthority.model';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DateInputHelper } from '../../../helpers/date-input.helper';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UserService } from '../../../services/user.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { DecimalPipe } from '@angular/common';
import { CurrentUserService } from '../../../services/currentUser.service';
import { Subject } from 'rxjs';
import {
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { HighlightStatusDirective } from '../../../directives/highlight-status.directive';
import { PdfButtonComponent } from '../pdf-button/pdf-button.component';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { DatePipe } from '../../../pipes/date.pipe';
import { TimePipe } from '../../../pipes/time.pipe';
import { Job } from '../../../model/job.model';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmBoxComponent } from '../../../components/confirm-box/confirm-box.component';
import { mediumModalConfig } from '../../../consts/modal-config.const';
import { ModalConfigsHelper } from '../../../helpers/modal-configs.helper';
import { MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';

@Component({
  selector: 'app-pre-authority-curtain',
  templateUrl: './pre-authority-curtain.component.html',
  styleUrls: ['./pre-authority-curtain.component.scss'],
  providers: [DecimalPipe],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    HighlightStatusDirective,
    MatExpansionPanelDescription,
    PdfButtonComponent,
    MatDivider,
    MatIcon,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatSuffix,
    MatError,
    DecimalPipe,
    DatePipe,
    TimePipe,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuContent,
    MatMenuItem
  ]
})
export class PreAuthorityCurtainComponent implements OnInit, OnChanges, OnDestroy {
  @Input() preAuthority: PreAuthority;
  @Input() jobId: number;
  @Input() editable: boolean;
  @Input() ownerContributionEditable: boolean;
  @Input() quoteSubmitted: boolean;
  @Output() disableSendButton: EventEmitter<boolean> = new EventEmitter();
  @Output() refreshJobViewer: EventEmitter<any> = new EventEmitter();
  @Output() synchronizeOwnerContribution: EventEmitter<Job> = new EventEmitter();
  @Output() openPdfReport: EventEmitter<null> = new EventEmitter<null>();

  timezone: string;
  preAuthorityCurtainOpened = false;
  saving = false;
  preAuthorityForm: UntypedFormGroup;
  private unsubscribe = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    private currentUserService: CurrentUserService,
    private message: MessagingService,
    private decPipe: DecimalPipe,
    public toolbarService: ToolbarService,
    public dateInputHelper: DateInputHelper,
    private dialog: MatDialog,
    private modalConfigsHelper: ModalConfigsHelper
  ) {}

  get ownerContribution() {
    return this.preAuthorityForm.get('ownerContribution');
  }

  ngOnInit(): void {
    this.timezone = this.currentUserService?.timeZone;
    this.preAuthorityForm = this.fb.group({
      ownerContribution: [
        this.preAuthority.ownerContribution,
        Validators.pattern('\\-?(\\d+|\\d{1,3}(,\\d{3})*)(\\.\\d{1,2})?')
      ]
    });

    this.formatOwnerContribution();

    this.ownerContribution.valueChanges.pipe(takeUntil(this.unsubscribe), distinctUntilChanged()).subscribe(() => {
      if (this.ownerContribution.valid) {
        this.disableSendButton.emit(false);
        this.updateJob('ownerContribution');
      } else {
        this.ownerContribution.markAsTouched();
        this.disableSendButton.emit(true);
      }
    });

    this.editable ? this.preAuthorityForm.enable() : this.preAuthorityForm.disable();
    this.ownerContributionEditable ? this.ownerContribution.enable() : this.ownerContribution.disable();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.preAuthority?.firstChange) {
      this.formatOwnerContribution();
    }
    if (changes.ownerContributionEditable && !changes.ownerContributionEditable.firstChange) {
      this.ownerContributionEditable ? this.ownerContribution.enable() : this.ownerContribution.disable();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  confirmWithdrawSelfAuthority(): void {
    const data = {
      title: 'Withdraw Self-Authority',
      alertMessage: 'Your self-authority will be withdrawn.',
      confirmButton: 'WITHDRAW',
      biggerButtons: true
    };
    this.dialog
      .open(ConfirmBoxComponent, this.modalConfigsHelper.buildMediumModalConfig(data, mediumModalConfig))
      .afterClosed()
      .subscribe((result) => {
        if (result === 'WITHDRAW') {
          this.toolbarService.setCloudState('SAVING');
          this.userService.updateJob('', this.jobId, 'withdrawSelfAuthority').subscribe({
            next: () => {
              this.message.confirm('Self-authority withdrawn');
              this.toolbarService.setCloudState('RESTING');
              this.refreshJobViewer.emit();
            },
            error: () => {
              this.message.error('Could not withdraw self-authority.');
              this.toolbarService.setCloudState('RESTING');
            }
          });
        }
      });
  }

  updateJob(fieldName: string) {
    this.saving = true;
    const fieldValue = this.ownerContribution.value
      ? this.ownerContribution.value.toString().replace(',', '')
      : this.ownerContribution.value;

    if (this.preAuthorityForm.controls[fieldName].valid) {
      this.toolbarService.setCloudState('SAVING');
      this.userService.updateJob(fieldValue, this.jobId, fieldName).subscribe({
        next: (job: Job) => {
          this.toolbarService.setCloudState('RESTING');
          this.synchronizeOwnerContribution.emit(job);
          this.preAuthority.ownerContribution = this.ownerContribution.value;
          this.saving = false;
        },
        error: () => {
          this.message.error('Could not update field.');
          this.toolbarService.setCloudState('RESTING');
          this.saving = false;
        }
      });
    }
  }

  formatOwnerContribution() {
    if (this.saving) {
      this.ownerContribution.setValue(this.decPipe.transform(this.ownerContribution.value, '1.2-2'), {
        emitEvent: false
      });
    } else {
      this.ownerContribution.setValue(this.decPipe.transform(this.preAuthority.ownerContribution, '1.2-2'), {
        emitEvent: false
      });
    }
  }
}
